import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Row, Col, Select } from 'antd';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import {
  useForm,
  Controller,
  Control,
  FieldErrors,
  FieldValues,
} from 'react-hook-form';
import { AlertBox, Button, Loader, ModalInfo, SvgIcon } from '@/components';
import {
  AlertRule,
  AlertRuleTxCountParams,
  AlertRuleType,
  AlertRuleIntervalType,
  AlertRuleDirection,
  AlertRuleCategoryJoin,
  CompRiskLevel,
} from '@/api';
import { RiskKeys } from '@/config';
import { sanitizeNumericInput } from '@/helpers';
import { alertRulesActions } from '@/redux/actions';
import {
  alertRulesSelectors,
  complianceAlertLevelsSelectors,
} from '@/redux/selectors';
import { AlertRuleFormFields } from '../AlertRuleForm/AlertRuleFormFields';
import {
  selectPopupStyles,
  DEFAULT_TX_COUNT_RULE,
  type AlertGradeOption,
  type AlertDirectionOption,
  type AlertEntityTypeOption,
} from '../AlertRuleForm/constants';
import { validateMinAmount, validateMaxAmount } from '../AlertRuleForm/helpers';
import styles from './TransferCountRuleModal.module.sass';

type TransferCountFormValues = {
  name: string;
  risk: CompRiskLevel;
  direction: AlertRuleDirection;
  categories: RiskKeys[];
  categoryJoin: AlertRuleCategoryJoin;
  params: {
    count: string;
    intervalType: AlertRuleIntervalType;
    intervalValue: string;
    minAmount: string;
    maxAmount: string;
  };
};

interface TransferCountRuleModalProps {
  isOpen: boolean;
  isLoading: boolean;
  editRuleId: AlertRule['id'] | null;
  onClose: () => void;
}

export const TransferCountRuleModal = ({
  isOpen,
  isLoading,
  editRuleId,
  onClose,
}: TransferCountRuleModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isAdd = !editRuleId;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const alertRulesData = useSelector(alertRulesSelectors.getAlertRulesData);
  const alertRulesError = useSelector(alertRulesSelectors.getAlertRulesError);
  const complianceAlertLevelsData = useSelector(
    complianceAlertLevelsSelectors.getComplianceAlertLevelsSlice,
  );

  const editRuleData = useMemo(() => {
    if (!editRuleId) return null;
    return alertRulesData.data?.find(
      (rule: AlertRule) => String(rule.id) === String(editRuleId),
    );
  }, [editRuleId, alertRulesData]);

  const defaultParams = DEFAULT_TX_COUNT_RULE.params as AlertRuleTxCountParams;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<TransferCountFormValues>({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      risk: CompRiskLevel.Low,
      direction: 'income',
      categories: [RiskKeys.MERCHANT_SERVICES],
      categoryJoin: 'all',
      params: {
        count: String(defaultParams.count || ''),
        intervalType: defaultParams.intervalType,
        intervalValue: String(defaultParams.intervalValue || ''),
        minAmount: defaultParams.minAmount
          ? String(defaultParams.minAmount)
          : '',
        maxAmount: defaultParams.maxAmount
          ? String(defaultParams.maxAmount)
          : '',
      },
    },
  });

  useEffect(() => {
    if (!isOpen) setIsSubmitting(false);
  }, [isOpen]);

  useEffect(() => {
    if (
      editRuleData &&
      editRuleId &&
      editRuleData.type === AlertRuleType.TX_COUNT
    ) {
      const ruleParams = editRuleData.params as AlertRuleTxCountParams;
      const editCategories =
        (editRuleData.categories?.length ?? 0) > 0
          ? editRuleData.categories!
          : DEFAULT_TX_COUNT_RULE.categories ?? [];

      reset({
        name: editRuleData.name,
        risk: editRuleData.risk,
        direction: editRuleData.direction,
        categories: editCategories,
        categoryJoin: editRuleData.categoryJoin ?? 'all',
        params: {
          count: String(ruleParams?.count ?? ''),
          intervalType: ruleParams?.intervalType ?? 'minute',
          intervalValue: String(ruleParams?.intervalValue ?? ''),
          minAmount: String(ruleParams?.minAmount ?? ''),
          maxAmount: String(ruleParams?.maxAmount ?? ''),
        },
      });
      return;
    }

    if (!editRuleId) {
      reset({
        name: DEFAULT_TX_COUNT_RULE.name,
        risk: DEFAULT_TX_COUNT_RULE.risk,
        direction: DEFAULT_TX_COUNT_RULE.direction,
        categories: DEFAULT_TX_COUNT_RULE.categories ?? [],
        categoryJoin: DEFAULT_TX_COUNT_RULE.categoryJoin ?? 'all',
        params: {
          count: String(defaultParams.count || ''),
          intervalType: defaultParams.intervalType,
          intervalValue: String(defaultParams.intervalValue || ''),
          minAmount: defaultParams.minAmount
            ? String(defaultParams.minAmount)
            : '',
          maxAmount: defaultParams.maxAmount
            ? String(defaultParams.maxAmount)
            : '',
        },
      });
    }
  }, [editRuleId, editRuleData, reset, defaultParams]);

  useEffect(() => {
    if (isSubmitting && !isLoading && !alertRulesError) {
      setIsSubmitting(false);
      reset();
      onClose();
    }
  }, [isSubmitting, isLoading, alertRulesError, reset, onClose]);

  const alertGradeOptions: AlertGradeOption[] = [
    { label: t('translation.comp_risk_label_low'), value: CompRiskLevel.Low },
    {
      label: t('translation.comp_risk_label_medium'),
      value: CompRiskLevel.Medium,
    },
    { label: t('translation.comp_risk_label_high'), value: CompRiskLevel.High },
    {
      label: t('translation.comp_risk_label_severe'),
      value: CompRiskLevel.Severe,
    },
  ];

  const alertDirectionOptions: AlertDirectionOption[] = [
    { label: t('translation.all'), value: 'all' },
    { label: `${t('translation.deposit')} ↓`, value: 'income' },
    { label: `${t('translation.withdrawal')} ↑`, value: 'outcome' },
  ];

  const alertEntityTypeOptions: AlertEntityTypeOption[] = (
    complianceAlertLevelsData?.data?.categories ?? []
  ).map((key) => ({ label: key, value: key }));

  const intervalOptions = [
    { value: 'minute', label: t('translation.minute') },
    { value: 'hour', label: t('translation.hour') },
    { value: 'day', label: t('translation.day') },
  ];

  const onSubmit = useCallback(
    (data: TransferCountFormValues) => {
      setIsSubmitting(true);
      const minVal = Number(data.params.minAmount);
      const maxVal = Number(data.params.maxAmount);
      const params: AlertRuleTxCountParams = {
        count: Number(data.params.count),
        intervalType: data.params.intervalType,
        intervalValue: Number(data.params.intervalValue),
        minAmount: minVal > 0 ? minVal : undefined,
        maxAmount: maxVal > 0 ? maxVal : undefined,
      };
      const payload = {
        name: data.name,
        risk: data.risk,
        direction: data.direction,
        categories: data.categories,
        categoryJoin: data.categoryJoin,
        type: AlertRuleType.TX_COUNT,
        params,
      };
      if (isAdd) {
        dispatch(alertRulesActions.createAlertRule(payload));
      } else if (editRuleId) {
        dispatch(
          alertRulesActions.updateAlertRule({ id: editRuleId, ...payload }),
        );
      }
    },
    [isAdd, editRuleId, dispatch],
  );

  const handleOnSave = handleSubmit(onSubmit);

  return (
    <ModalInfo
      isOpen={isOpen}
      canClose={!isLoading}
      title={isAdd ? t('translation.add_rule') : t('translation.edit_rule')}
      actions={
        <div className={styles.actions}>
          <Button
            size="s"
            type="secondary"
            corner="rect"
            minWidth={92}
            onClick={onClose}
          >
            {t('translation.cancel_btn')}
          </Button>
          <Button
            size="s"
            type="primary"
            corner="rect"
            minWidth={92}
            onClick={handleOnSave}
          >
            {isAdd ? t('translation.add') : t('translation.save')}
          </Button>
        </div>
      }
      className={styles.modal}
      onClose={onClose}
    >
      {alertRulesError && (
        <AlertBox type="danger">{{ description: alertRulesError }}</AlertBox>
      )}

      <div className={styles.form}>
        <AlertRuleFormFields
          control={control as unknown as Control<FieldValues>}
          errors={errors as unknown as FieldErrors<FieldValues>}
          alertGradeOptions={alertGradeOptions}
          alertDirectionOptions={alertDirectionOptions}
          alertEntityTypeOptions={alertEntityTypeOptions}
        />

        <div className={styles.group}>
          <Row gutter={20}>
            <Col xs={24} lg={12}>
              <div className={styles.field}>
                <div className={styles.label}>
                  {t('translation.number_of_transfers')}
                  <sup>*</sup>
                </div>
                <div className={styles.fieldRow}>
                  <Controller
                    name="params.count"
                    control={control}
                    rules={{ required: true, validate: (v) => Number(v) >= 1 }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        size="large"
                        id="count"
                        className={styles.input}
                        onChange={(e) =>
                          sanitizeNumericInput(e, field.onChange)
                        }
                        status={errors.params?.count ? 'error' : undefined}
                      />
                    )}
                  />
                </div>
                <small className={styles.note}>
                  {t('translation.number_of_transfers_note')}
                </small>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <div className={styles.field}>
                <div className={styles.label}>
                  {t('translation.time_period')}
                  <sup>*</sup>
                </div>
                <div className={styles.fieldRow}>
                  <Controller
                    name="params.intervalValue"
                    control={control}
                    rules={{ required: true, validate: (v) => Number(v) >= 1 }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        size="large"
                        id="intervalValue"
                        className={styles.input}
                        onChange={(e) =>
                          sanitizeNumericInput(e, field.onChange)
                        }
                        status={
                          errors.params?.intervalValue ? 'error' : undefined
                        }
                      />
                    )}
                  />
                  <Controller
                    name="params.intervalType"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        size="large"
                        options={intervalOptions}
                        placeholder={t('translation.select')}
                        styles={selectPopupStyles}
                        className={styles.selectWhite}
                      />
                    )}
                  />
                </div>
                <small className={styles.note}>
                  {t('translation.time_period_note')}
                </small>
              </div>
            </Col>
          </Row>

          <div className={styles.field}>
            <div className={styles.label}>
              {t('translation.amount_range')}
              <sup>*</sup>
            </div>
            <div className={cx(styles.fields, styles.amountRange)}>
              <div className={styles.field}>
                <Controller
                  name="params.minAmount"
                  control={control}
                  rules={{
                    validate: (v) =>
                      validateMinAmount(
                        v ?? '',
                        watch('params.maxAmount') ?? '',
                      ),
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      prefix={
                        <SvgIcon name="dollar" className={styles.prefix} />
                      }
                      size="large"
                      id="range_from"
                      className={styles.input}
                      onChange={(e) => sanitizeNumericInput(e, field.onChange)}
                      status={errors.params?.minAmount ? 'error' : undefined}
                    />
                  )}
                />
              </div>
              <div className={styles.field}>
                <Controller
                  name="params.maxAmount"
                  control={control}
                  rules={{
                    validate: (v) =>
                      validateMaxAmount(
                        v ?? '',
                        watch('params.minAmount') ?? '',
                      ),
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      prefix={
                        <SvgIcon name="dollar" className={styles.prefix} />
                      }
                      size="large"
                      id="range_to"
                      onChange={(e) => sanitizeNumericInput(e, field.onChange)}
                      status={errors.params?.maxAmount ? 'error' : undefined}
                    />
                  )}
                />
              </div>
              <div className={styles.separator} />
            </div>
            <small className={styles.note}>
              {t('translation.amount_range_note')}
            </small>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className={styles.loader}>
          <Loader size="medium" />
        </div>
      )}
    </ModalInfo>
  );
};
